<template>
  <div v-if="isDeveloper" class="on-screen-debugger-root" dir="ltr">
    <div class="on-screen-debugger" dir="ltr">
      <div class="on-screen-debugger__toolbar">
        <span class="on-screen-debugger__toolbar-title">Codes</span>
        <span class="on-screen-debugger__toolbar-codes">
          <strong>1</strong> Toggle panel <strong>2</strong> Console <strong>3</strong> Network <strong>4</strong> Pause
          <strong>5</strong> Video <strong>6</strong> General <strong>0</strong> Clear all
        </span>
        <span class="on-screen-debugger__toolbar-counts">
          <span v-if="paused" class="on-screen-debugger__toolbar-paused">Paused</span>
          {{ consoleCount }} logs · {{ networkCount }} requests
        </span>
      </div>
      <div v-show="panelExpanded" class="on-screen-debugger__panel">
        <div class="on-screen-debugger__tabs">
          <button
            type="button"
            class="on-screen-debugger__tab"
            :class="{ 'on-screen-debugger__tab--active': activeTab === 'console' }"
            @click="showConsoleTab()"
          >
            Console
          </button>
          <button
            type="button"
            class="on-screen-debugger__tab"
            :class="{ 'on-screen-debugger__tab--active': activeTab === 'network' }"
            @click="showNetworkTab()"
          >
            Network
          </button>
          <button
            type="button"
            class="on-screen-debugger__tab"
            :class="{ 'on-screen-debugger__tab--active': activeTab === 'video' }"
            @click="showVideoTab()"
          >
            Video
          </button>
          <button
            type="button"
            class="on-screen-debugger__tab"
            :class="{ 'on-screen-debugger__tab--active': activeTab === 'general' }"
            @click="showGeneralTab()"
          >
            General
          </button>
        </div>
        <div ref="contentRef" class="on-screen-debugger__content">
          <div v-show="activeTab === 'console'" class="on-screen-debugger__list on-screen-debugger__list--console">
            <div
              v-for="entry in consoleLogs"
              :key="entry.id"
              class="on-screen-debugger__row on-screen-debugger__row--console"
            >
              <span class="on-screen-debugger__time">{{ entry.time }}</span>
              <span class="on-screen-debugger__level" :class="`on-screen-debugger__level--${entry.level}`">
                [{{ entry.level.toUpperCase() }}]
              </span>
              <span class="on-screen-debugger__msg">{{ entry.message }}</span>
            </div>
            <div v-if="consoleLogs.length === 0" class="on-screen-debugger__empty">
              No console entries. Press 2 to focus console.
            </div>
          </div>
          <div v-show="activeTab === 'network'" class="on-screen-debugger__list on-screen-debugger__list--network">
            <div class="on-screen-debugger__row on-screen-debugger__row--header">
              <span class="on-screen-debugger__col on-screen-debugger__col--time">Time</span>
              <span class="on-screen-debugger__col on-screen-debugger__col--domain">Domain</span>
              <span class="on-screen-debugger__col on-screen-debugger__col--method">Type</span>
              <span class="on-screen-debugger__col on-screen-debugger__col--name">Asset</span>
              <span class="on-screen-debugger__col on-screen-debugger__col--status">Status</span>
              <span class="on-screen-debugger__col on-screen-debugger__col--timing">Timing</span>
              <span class="on-screen-debugger__col on-screen-debugger__col--size">Size</span>
            </div>
            <div
              v-for="entry in networkLogs"
              :key="entry.id"
              class="on-screen-debugger__row on-screen-debugger__row--network"
              :class="{ 'on-screen-debugger__row--network--failed': isFailedRequest(entry) }"
            >
              <span class="on-screen-debugger__col on-screen-debugger__col--time">{{ entry.time }}</span>
              <span class="on-screen-debugger__col on-screen-debugger__col--domain">{{ entry.domain }}</span>
              <span class="on-screen-debugger__col on-screen-debugger__col--method">{{ entry.method }}</span>
              <span class="on-screen-debugger__col on-screen-debugger__col--name" :title="entry.name">{{
                entry.name
              }}</span>
              <span
                class="on-screen-debugger__col on-screen-debugger__col--status"
                :class="{ 'on-screen-debugger__col--status--failed': isFailedRequest(entry) }"
                >{{ entry.status }}</span
              >
              <span class="on-screen-debugger__col on-screen-debugger__col--timing">{{ entry.timing }}</span>
              <span class="on-screen-debugger__col on-screen-debugger__col--size">{{ entry.size }}</span>
            </div>
            <div v-if="networkLogs.length === 0" class="on-screen-debugger__empty">
              No network entries. Press 3 to focus network.
            </div>
          </div>
          <div v-show="activeTab === 'video'" class="on-screen-debugger__list on-screen-debugger__list--video">
            <OnScreenDebuggerVideoTab />
            <div v-if="!videoTabHasData" class="on-screen-debugger__empty">
              No video data. Open a stream and press 5 to focus video.
            </div>
          </div>
          <div v-show="activeTab === 'general'" class="on-screen-debugger__list on-screen-debugger__list--general">
            <OnScreenDebuggerGeneralTab />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, computed, ref, watch, nextTick } from 'vue';
import { useDeveloperCheck } from '@/utils/useDeveloperCheck';
import { useOnScreenDebugger } from '@/composables/useOnScreenDebugger';
import { useVideoDebugStore } from '@/store/video-debug';
import OnScreenDebuggerVideoTab from './OnScreenDebuggerVideoTab.vue';
import OnScreenDebuggerGeneralTab from './OnScreenDebuggerGeneralTab.vue';

const { isDeveloper, fetchDeveloperIds } = useDeveloperCheck();
const debuggerApi = useOnScreenDebugger(isDeveloper);
const videoDebugStore = useVideoDebugStore();

const contentRef = ref<HTMLElement | null>(null);

const panelExpanded = computed(() => debuggerApi.panelExpanded.value);
const paused = computed(() => debuggerApi.paused.value);
const activeTab = computed(() => debuggerApi.activeTab.value);
const consoleLogs = computed(() => debuggerApi.consoleLogs.value);
const networkLogs = computed(() => debuggerApi.networkLogs.value);
const consoleCount = computed(() => debuggerApi.consoleCount.value);
const networkCount = computed(() => debuggerApi.networkCount.value);

const videoTabHasData = computed(
  () =>
    !!videoDebugStore.manifestUrl.value ||
    !!videoDebugStore.playbackInfo.value ||
    !!videoDebugStore.deviceCapabilities.value,
);

function scrollToBottom() {
  nextTick(() => {
    const el = contentRef.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
}

watch([consoleLogs, networkLogs], () => scrollToBottom(), { deep: true });
watch(activeTab, () => scrollToBottom());

function showConsoleTab() {
  debuggerApi.showConsoleTab();
}
function showNetworkTab() {
  debuggerApi.showNetworkTab();
}
function showVideoTab() {
  debuggerApi.showVideoTab();
}
function showGeneralTab() {
  debuggerApi.showGeneralTab();
}

function isFailedRequest(entry: { status: number | string }): boolean {
  const s = entry.status;
  if (s === 'ERR' || s === 0) return true;
  const n = Number(s);
  return !Number.isNaN(n) && n >= 400;
}

function handleKeyDown(event: KeyboardEvent) {
  if (!isDeveloper.value) return;
  const key = event.key;
  const code = event.keyCode;
  if (key === '0' || code === 48) {
    event.preventDefault();
    event.stopPropagation();
    debuggerApi.clearAll();
    return;
  }
  if (key === '1' || code === 49) {
    event.preventDefault();
    event.stopPropagation();
    debuggerApi.togglePanel();
    return;
  }
  if (key === '2' || code === 50) {
    event.preventDefault();
    event.stopPropagation();
    debuggerApi.showConsoleTab();
    return;
  }
  if (key === '3' || code === 51) {
    event.preventDefault();
    event.stopPropagation();
    debuggerApi.showNetworkTab();
    return;
  }
  if (key === '4' || code === 52) {
    event.preventDefault();
    event.stopPropagation();
    debuggerApi.togglePause();
    return;
  }
  if (key === '5' || code === 53) {
    event.preventDefault();
    event.stopPropagation();
    debuggerApi.showVideoTab();
    return;
  }
  if (key === '6' || code === 54) {
    event.preventDefault();
    event.stopPropagation();
    debuggerApi.showGeneralTab();
    return;
  }
}

onMounted(() => {
  fetchDeveloperIds();
  document.addEventListener('keydown', handleKeyDown, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeyDown, true);
  debuggerApi.closeDebugger();
});
</script>

<style lang="less" scoped>
.on-screen-debugger-root {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9999;
  pointer-events: none;
  direction: ltr;
  text-align: left;
}

.on-screen-debugger-root .on-screen-debugger {
  pointer-events: auto;
}

.on-screen-debugger {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  color: #e0e0e0;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 12px;
}

.on-screen-debugger__toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.85);
  border-bottom: 1px solid rgba(0, 200, 180, 0.4);
  color: #fff;
}

.on-screen-debugger__toolbar-title {
  color: #00c8b4;
  font-weight: 600;
  margin-right: 8px;
}

.on-screen-debugger__toolbar-codes {
  flex: 1;
  color: #b0b0b0;
}

.on-screen-debugger__toolbar-codes strong {
  color: #00ff88;
  margin: 0 2px 0 6px;
}

.on-screen-debugger__toolbar-counts {
  color: #00c8b4;
  font-size: 11px;
}

.on-screen-debugger__toolbar-paused {
  color: #ffaa00;
  margin-right: 6px;
}

.on-screen-debugger__panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: rgba(18, 18, 22, 0.92);
}

.on-screen-debugger__tabs {
  flex-shrink: 0;
  display: flex;
  gap: 0;
  padding: 6px 12px 0;
  border-bottom: 1px solid rgba(0, 200, 180, 0.35);
}

.on-screen-debugger__tab {
  padding: 6px 14px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: #888;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
}

.on-screen-debugger__tab:hover {
  color: #00c8b4;
}

.on-screen-debugger__tab--active {
  color: #00c8b4;
  border-bottom-color: #00c8b4;
}

.on-screen-debugger__content {
  flex: 1;
  overflow: auto;
  padding: 0 8px 8px 8px;
}

.on-screen-debugger__list {
  min-height: 80px;
}

.on-screen-debugger__list--console {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.on-screen-debugger__row--console {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 2px 0;
  word-break: break-word;
}

.on-screen-debugger__time {
  flex-shrink: 0;
  color: #00c8b4;
  font-size: 11px;
}

.on-screen-debugger__level {
  flex-shrink: 0;
  font-size: 10px;
}

.on-screen-debugger__level--log {
  color: #00ff88;
}

.on-screen-debugger__level--warn {
  color: #ffaa00;
}

.on-screen-debugger__level--error {
  color: #ff5555;
}

.on-screen-debugger__level--info,
.on-screen-debugger__level--debug {
  color: #88aaff;
}

.on-screen-debugger__msg {
  flex: 1;
  color: #e0e0e0;
}

.on-screen-debugger__list--network {
  display: flex;
  flex-direction: column;
}

.on-screen-debugger__list--network .on-screen-debugger__row--header,
.on-screen-debugger__list--network .on-screen-debugger__row--network {
  margin-bottom: 2px;
}

.on-screen-debugger__row--header {
  display: grid;
  grid-template-columns: 90px 85px 50px 1fr 52px 56px 56px;
  padding: 4px 6px;
  color: #00c8b4;
  font-weight: 600;
  font-size: 11px;
  border-bottom: 1px solid rgba(0, 200, 180, 0.3);
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgba(18, 18, 22, 0.98);
}

.on-screen-debugger__row--network {
  display: grid;
  grid-template-columns: 90px 85px 50px 1fr 52px 56px 56px;
  padding: 2px 6px;
  align-items: center;
}

/* Chromium 53 doesn't support grid-gap; use padding on cells for column spacing */
.on-screen-debugger__row--header .on-screen-debugger__col,
.on-screen-debugger__row--network .on-screen-debugger__col {
  padding-left: 4px;
  padding-right: 4px;
}

.on-screen-debugger__row--header .on-screen-debugger__col:first-child,
.on-screen-debugger__row--network .on-screen-debugger__col:first-child {
  padding-left: 0;
}

.on-screen-debugger__row--header .on-screen-debugger__col:last-child,
.on-screen-debugger__row--network .on-screen-debugger__col:last-child {
  padding-right: 0;
}

.on-screen-debugger__row--network .on-screen-debugger__col--status {
  color: #00ff88;
}

.on-screen-debugger__row--network--failed {
  color: #ff5555;
}

.on-screen-debugger__row--network--failed .on-screen-debugger__col--status,
.on-screen-debugger__col--status--failed {
  color: #ff5555;
  font-weight: 600;
}

.on-screen-debugger__col--time {
  min-width: 0;
}

.on-screen-debugger__col--domain {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.on-screen-debugger__col--name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.on-screen-debugger__empty {
  padding: 16px;
  color: #666;
  font-style: italic;
}
</style>
